import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getAttendance, User, StaffCategory, downloadAttendanceReportPDF, downloadAttendanceReportExcel } from '../../services/api';
import type { Attendance } from '../../services/api';
import { getMaxDateToday, getMaxEndDateForStart } from '../../utils/dateUtils';

interface AttendanceReportProps {
  allUsers: User[];
  staffCategories: StaffCategory[];
}

type DateFilterType = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

const AttendanceReport: React.FC<AttendanceReportProps> = ({ allUsers, staffCategories }) => {
  const [attendanceRecords, setAttendanceRecords] = useState<Attendance[]>([]);
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>('');
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentDate, setCurrentDate] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });

  // Monitor theme changes
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadMenu]);

  // Check if date has changed and refresh if filter is "today"
  useEffect(() => {
    const checkDateChange = () => {
      const today = new Date();
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      if (todayString !== currentDate) {
        setCurrentDate(todayString);
        // If filter is "today", refresh the data
        if (dateFilterType === 'today') {
          fetchAttendanceData();
        }
      }
    };

    // Check immediately
    checkDateChange();

    // Set up interval to check every minute
    const interval = setInterval(checkDateChange, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, dateFilterType]);

  useEffect(() => {
    fetchAttendanceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterType, customStartDate, customEndDate]);

  const fetchAttendanceData = async () => {
    try {
      const getDateRange = () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        switch (dateFilterType) {
          case 'today': {
            const start = new Date(today);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return { start: start.getTime(), end: end.getTime() };
          }
          case 'yesterday': {
            const start = new Date(today);
            start.setDate(start.getDate() - 1);
            const end = new Date(start);
            end.setHours(23, 59, 59, 999);
            return { start: start.getTime(), end: end.getTime() };
          }
          case 'thisWeek': {
            const start = new Date(today);
            const dayOfWeek = start.getDay();
            const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return { start: start.getTime(), end: end.getTime() };
          }
          case 'lastWeek': {
            const start = new Date(today);
            const dayOfWeek = start.getDay();
            const diff = start.getDate() - dayOfWeek - 6;
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);
            end.setHours(23, 59, 59, 999);
            return { start: start.getTime(), end: end.getTime() };
          }
          case 'thisMonth': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return { start: start.getTime(), end: end.getTime() };
          }
          case 'lastMonth': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start.setHours(0, 0, 0, 0);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            end.setHours(23, 59, 59, 999);
            return { start: start.getTime(), end: end.getTime() };
          }
          case 'custom': {
            if (!customStartDate || !customEndDate) {
              return null;
            }
            const maxEndStr = getMaxEndDateForStart(customStartDate);
            const endStr = maxEndStr && customEndDate > maxEndStr ? maxEndStr : customEndDate;
            const start = new Date(customStartDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endStr);
            end.setHours(23, 59, 59, 999);
            return { start: start.getTime(), end: end.getTime() };
          }
          default:
            return null;
        }
      };

      const dateRange = getDateRange();
      if (!dateRange) {
        setAttendanceRecords([]);
        return;
      }

      const response = await getAttendance(1, 1000, undefined, dateRange.start, dateRange.end);
      setAttendanceRecords(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load attendance data:', err);
      setAttendanceRecords([]);
    }
  };

  // Helper function to get date range for generating date columns
  const getDateRangeForColumns = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateFilterType) {
      case 'today': {
        const date = new Date(today);
        date.setHours(0, 0, 0, 0);
        return [date];
      }
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        return [yesterday];
      }
      case 'thisWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const dates = [];
        const currentDate = new Date(start);
        while (currentDate <= today) {
          dates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        return dates;
      }
      case 'lastWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek - 6;
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const dates = [];
        const currentDate = new Date(start);
        for (let i = 0; i < 7; i++) {
          dates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        return dates;
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        const dates = [];
        const currentDate = new Date(start);
        while (currentDate <= today) {
          dates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        return dates;
      }
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        const dates = [];
        const currentDate = new Date(start);
        while (currentDate <= end) {
          dates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        return dates;
      }
      case 'custom': {
        if (!customStartDate || !customEndDate) {
          return [];
        }
        const maxEndStr = getMaxEndDateForStart(customStartDate);
        const endStr = maxEndStr && customEndDate > maxEndStr ? maxEndStr : customEndDate;
        const start = new Date(customStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endStr);
        end.setHours(23, 59, 59, 999);
        const dates = [];
        const currentDate = new Date(start);
        while (currentDate <= end) {
          dates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        return dates;
      }
      default:
        return [];
    }
  };

  // Group attendance by user
  const groupedByUser = attendanceRecords.reduce((acc, record) => {
    const userId = record.spec.userId;
    if (!acc[userId]) {
      acc[userId] = [];
    }
    acc[userId].push(record);
    return acc;
  }, {} as Record<string, Attendance[]>);

  // Get user info helper
  const getUserInfo = (userId: string) => {
    return allUsers.find(u => (u._id || u.id) === userId);
  };

  // Get category display name helper
  const getCategoryDisplayName = (categoryName: string | undefined) => {
    if (!categoryName) return '—';
    const category = staffCategories.find(
      c => c.metadata.name === categoryName || c.metadata.displayName === categoryName
    );
    return category ? (category.metadata.displayName || category.metadata.name) : categoryName;
  };

  // Get attendance for a specific date
  const getAttendanceForDate = (userId: string, date: Date) => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    
    const records = groupedByUser[userId] || [];
    return records.find(r => {
      const checkInTime = r.spec.checkIn.time;
      return checkInTime >= startOfDay.getTime() && checkInTime <= endOfDay.getTime();
    });
  };

  // Format time
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  // Format date as DD-MM-YY
  const formatDateDDMMYY = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Get date range for API call
  const getDateRangeForAPI = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateFilterType) {
      case 'today': {
        const start = new Date(today);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'yesterday': {
        const start = new Date(today);
        start.setDate(start.getDate() - 1);
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek - 6;
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'custom': {
        if (!customStartDate || !customEndDate) {
          return null;
        }
        const maxEndStr = getMaxEndDateForStart(customStartDate);
        const endStr = maxEndStr && customEndDate > maxEndStr ? maxEndStr : customEndDate;
        const start = new Date(customStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endStr);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      default:
        return null;
    }
  };

  const handleDownload = async (format: 'pdf' | 'excel') => {
    try {
      setDownloading(true);
      setDownloadError('');
      setShowDownloadMenu(false);
      
      const dateRange = getDateRangeForAPI();
      if (!dateRange) {
        setDownloadError('Please select a valid date range');
        setDownloading(false);
        return;
      }
      
      let response;
      let fileExtension;
      
      if (format === 'pdf') {
        response = await downloadAttendanceReportPDF(dateRange.start, dateRange.end);
        fileExtension = 'pdf';
      } else {
        response = await downloadAttendanceReportExcel(dateRange.start, dateRange.end);
        fileExtension = 'xlsx';
      }
      
      const { url } = response.data.data;
      
      // Create a temporary anchor element to download the file
      const link = document.createElement('a');
      link.href = url;
      const dateRangeStr = dateFilterType === 'custom' 
        ? `${customStartDate}_to_${customEndDate}`
        : dateFilterType;
      link.download = `attendance-report-${dateRangeStr}.${fileExtension}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading ${format.toUpperCase()}:`, err);
      setDownloadError(err.response?.data?.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  const dateColumns = getDateRangeForColumns();
  
  // Show all users, not just those with attendance records
  const allUserIds = allUsers.map(u => u._id || u.id || '').filter(id => id);

  // Filter users based on search term
  const filteredUserIds = useMemo(() => {
    if (!searchTerm.trim()) return allUserIds;
    
    const searchLower = searchTerm.toLowerCase();
    return allUserIds.filter((userId) => {
      const user = getUserInfo(userId);
      if (!user) return false;
      
      const name = (user.metadata?.name || user.name || '').toLowerCase();
      const roles = (user.spec?.roles || user.roles || []).join(', ').toLowerCase();
      const category = getCategoryDisplayName(user.spec?.staffCategory).toLowerCase();
      
      return (
        name.includes(searchLower) ||
        roles.includes(searchLower) ||
        category.includes(searchLower)
      );
    });
  }, [allUserIds, searchTerm, allUsers, staffCategories, getUserInfo, getCategoryDisplayName]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: downloadError ? '10px' : '20px', flexWrap: 'wrap', gap: '15px' }}>
        {/* <h3 style={{ margin: 0 }}>Checked In Users</h3> */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
          <input
            type="text"
            placeholder="Search by name or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '16px',
              width: '220px',
              flex: '0 0 220px',
              border: '1px solid',
              borderColor: isDarkTheme ? '#334155' : '#ddd',
              height: '36px',
              boxSizing: 'border-box',
            }}
          />
          
          {/* <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>Filter:</label> */}
          <select
            value={dateFilterType}
            onChange={(e) => setDateFilterType(e.target.value as DateFilterType)}
            className="reports-filter-select"
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '16px',
              minWidth: '150px',
            }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="thisWeek">This Week</option>
            <option value="lastWeek">Last Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom Dates</option>
          </select>
          <div style={{ position: 'relative' }} ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={downloading || allUserIds.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: downloading || allUserIds.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                opacity: downloading || allUserIds.length === 0 ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {downloading ? 'Generating...' : 'Download'}
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ marginLeft: '6px' }}
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
            {showDownloadMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '5px',
                  backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                  border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  minWidth: '150px',
                }}
              >
                <button
                  onClick={() => handleDownload('pdf')}
                  disabled={downloading}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: 'transparent',
                    color: isDarkTheme ? '#ffffff' : '#333',
                    border: 'none',
                    borderRadius: '6px 6px 0 0',
                    cursor: downloading ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    textAlign: 'left',
                    opacity: downloading ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!downloading) {
                      e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Download as PDF
                </button>
                <button
                  onClick={() => handleDownload('excel')}
                  disabled={downloading}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: 'transparent',
                    color: isDarkTheme ? '#ffffff' : '#333',
                    border: 'none',
                    borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                    borderRadius: '0 0 6px 6px',
                    cursor: downloading ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    textAlign: 'left',
                    opacity: downloading ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!downloading) {
                      e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Download as Excel
                </button>
              </div>
            )}
          </div>
        </div>
        {dateFilterType === 'custom' && (
            <div style={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
              <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>Start Date:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomStartDate(value);
                  if (value && customEndDate) {
                    const start = new Date(value);
                    const end = new Date(customEndDate);
                    const diffDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                    if (diffDays > 31) setCustomEndDate(getMaxEndDateForStart(value));
                  }
                }}
                max={getMaxDateToday()}
                className="reports-date-input"
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '16px',
                }}
              />
              <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>End Date:</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                min={customStartDate || undefined}
                max={customStartDate ? getMaxEndDateForStart(customStartDate) : getMaxDateToday()}
                className="reports-date-input"
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '16px',
                }}
              />
            </div>
          )}
      </div>
      {downloadError && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#fee', 
          border: '1px solid #fcc', 
          borderRadius: '4px',
          color: '#c33',
          fontSize: '16px',
          marginBottom: '20px'
        }}>
          {downloadError}
        </div>
      )}
      <div 
        className="table-container" 
        style={{
          height: 'calc(100vh - 360px)',
          overflowY: 'auto',
          overflowX: 'auto',
          // borderRadius: '12px',
          border: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
          backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
          // boxShadow: isDarkTheme 
            // ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' 
            // : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        }}
      >
        <table 
          className="bookings-table"
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            minWidth: '800px',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: '8px 10px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                  width: '180px', minWidth: '180px', maxWidth: '180px', wordBreak: 'break-word',
                }}
              >
                Name
              </th>
              <th
                style={{
                  padding: '8px 10px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  whiteSpace: 'nowrap',
                }}
              >
                Category
              </th>
              {dateColumns.map((date, idx) => (
                <th
                  key={idx}
                  style={{
                    padding: '8px 6px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: isDarkTheme ? '#94a3b8' : '#64748b',
                    backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                    borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                    borderLeft: idx === 0 
                      ? 'none' 
                      : isDarkTheme 
                        ? '1px solid #334155' 
                        : '1px solid #e2e8f0',
                    minWidth: '120px',
                  }}
                >
                  {formatDateDDMMYY(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUserIds.map((userId, rowIdx) => {
              const user = getUserInfo(userId);
              const baseBgColor = rowIdx % 2 === 0
                ? (isDarkTheme ? '#1e293b' : '#ffffff')
                : (isDarkTheme ? '#0f172a' : '#f8fafc');
              const hoverBgColor = isDarkTheme ? '#334155' : '#f1f5f9';
              
              return (
                <tr
                  key={userId}
                  style={{
                    backgroundColor: baseBgColor,
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = hoverBgColor;
                    const nameCell = e.currentTarget.querySelector('td:first-child') as HTMLElement;
                    if (nameCell) {
                      nameCell.style.backgroundColor = hoverBgColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = baseBgColor;
                    const nameCell = e.currentTarget.querySelector('td:first-child') as HTMLElement;
                    if (nameCell) {
                      nameCell.style.backgroundColor = baseBgColor;
                    }
                  }}
                >
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                      fontWeight: '500',
                      fontSize: '16px',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: baseBgColor,
                      zIndex: 1,
                      width: '180px', minWidth: '180px', maxWidth: '180px', wordBreak: 'break-word',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {user?.metadata?.name || user?.name || 'Unknown'}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#cbd5e1' : '#475569',
                      fontSize: '15px',
                    }}
                  >
                    {getCategoryDisplayName(user?.spec?.staffCategory)}
                  </td>
                  {dateColumns.map((date, idx) => {
                    const attendance = getAttendanceForDate(userId, date);
                    if (!attendance) {
                      return (
                        <td
                          key={idx}
                          style={{
                            padding: '8px 6px',
                            borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                            borderLeft: idx === 0 
                              ? 'none' 
                              : isDarkTheme 
                                ? '1px solid #334155' 
                                : '1px solid #e2e8f0',
                            textAlign: 'center',
                            color: isDarkTheme ? '#64748b' : '#94a3b8',
                            fontSize: '15px',
                          }}
                        >
                          <span
                            style={{
                              color: isDarkTheme ? '#475569' : '#cbd5e1',
                              fontStyle: 'italic',
                            }}
                          >
                            —
                          </span>
                        </td>
                      );
                    }
                    
                    const checkInTime = formatTime(attendance.spec.checkIn.time);
                    const checkOutTime = attendance.spec.checkOut?.time 
                      ? formatTime(attendance.spec.checkOut.time)
                      : null;
                    
                    return (
                      <td
                        key={idx}
                        style={{
                          padding: '6px',
                          borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                          borderLeft: idx === 0 
                            ? 'none' 
                            : isDarkTheme 
                              ? '1px solid #334155' 
                              : '1px solid #e2e8f0',
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              backgroundColor: isDarkTheme 
                                ? 'rgba(16, 185, 129, 0.15)' 
                                : '#ecfdf5',
                              color: isDarkTheme ? '#6ee7b7' : '#059669',
                              fontSize: '13px',
                              fontWeight: '600',
                              whiteSpace: 'nowrap',
                              border: isDarkTheme 
                                ? '1px solid rgba(16, 185, 129, 0.3)' 
                                : '1px solid #d1fae5',
                            }}
                          >
                            IN: {checkInTime}
                          </div>
                          {checkOutTime && (
                            <div
                              style={{
                                padding: '6px 10px',
                                borderRadius: '6px',
                                backgroundColor: isDarkTheme 
                                  ? 'rgba(239, 68, 68, 0.15)' 
                                  : '#fef2f2',
                                color: isDarkTheme ? '#fca5a5' : '#dc2626',
                                fontSize: '13px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                border: isDarkTheme 
                                  ? '1px solid rgba(239, 68, 68, 0.3)' 
                                  : '1px solid #fee2e2',
                              }}
                            >
                              OUT: {checkOutTime}
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceReport;

